import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import Reveal from './Reveal';

const ClosingCTA = () => {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const requestBrief = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      toast.error('Check that email address', {
        description: 'It needs to look like name@company.com.',
      });
      return;
    }

    setSending(true);
    try {
      const { error } = await supabase.functions.invoke('support-request', {
        body: {
          name: trimmed.split('@')[0],
          email: trimmed,
          topic: 'sales',
          contact_preference: 'email',
          message:
            'Homepage – game plan: requested the free game plan PDF. Send it manually until delivery is automated.',
          page_url: window.location.href,
        },
      });
      if (error) throw error;
      setSent(true);
      setEmail('');
      toast.success('Check your inbox. Your game plan is on its way.');
    } catch {
      toast.error('That didn’t go through', {
        description: 'Try again in a moment, or reach us from the Support page.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="bg-foreground py-20 text-background md:py-28">
      <div className="container-wide">
        <Reveal className="max-w-2xl">
          <h2 className="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
            Ready to stop losing deals to a <span className="text-brand">stale</span> menu?
          </h2>
          <p className="mt-5 text-lg text-background/70">
            Start with the inventory you already have. Your first menu takes minutes.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button asChild size="lg" className="bg-brand text-brand-foreground hover:bg-brand/90">
              <Link to="/auth?mode=signup">
                Build your first menu free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <p className="mt-5 text-sm text-background/70">
            Set up in 15 minutes · Nothing public, ever · Buyers never pay
          </p>
        </Reveal>

        <Reveal delay={100} className="mt-14 max-w-xl border-t border-white/10 pt-8">
          <p className="font-medium">Prices are down. Your playbook shouldn’t be.</p>
          <p className="mt-1 text-sm text-background/60">
            Get our free game plan for selling in a soft market, plus the occasional update on what’s moving.
          </p>

          {sent ? (
            <p className="mt-5 flex items-center gap-2 text-sm text-brand-text">
              <Check className="h-4 w-4" />
              Check your inbox. Your game plan is on its way.
            </p>
          ) : (
            <form onSubmit={requestBrief} className="mt-5 flex flex-col gap-3 sm:flex-row">
              <div className="flex-1">
                <Label htmlFor="brief-email" className="sr-only">
                  Work email
                </Label>
                <Input
                  id="brief-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="border-white/20 bg-white/5 text-background placeholder:text-background/40"
                />
              </div>
              <Button
                type="submit"
                disabled={sending}
                variant="outline"
                className="border-white/25 bg-transparent text-background hover:bg-white/10 hover:text-background"
              >
                {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Get the game plan
              </Button>
            </form>
          )}
          <p className="mt-3 text-xs text-background/50">
            Free. A few emails a month at most. Unsubscribe in one click.
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default ClosingCTA;